/**
 * API client. The TypeScript sibling of site/portal/assets/api.js, carrying
 * the same contracts:
 *
 * - No credential lives in this client. Sign-in exchanges an Identity Platform
 *   ID token once for an httpOnly cookie; the token is used and dropped.
 * - Identity Platform is called over REST, not the Firebase SDK — one endpoint
 *   does not justify 100kB and a second auth surface.
 * - Wrong-email and wrong-password both surface as `unauthenticated`;
 *   distinguishing them tells a prober which addresses have accounts.
 */

const IDENTITY_URL = 'https://identitytoolkit.googleapis.com/v1/accounts:';

export interface AuthConfig {
  driver: string;
  devMode: boolean;
  apiKey: string | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`API ${status} (${code})`);
    this.name = 'ApiError';
  }
}

let config: AuthConfig | null = null;

export async function request<T = unknown>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers: {
      Accept: 'application/json',
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    // 'same-origin' is the default; stating it keeps the credential visible in
    // the code — nothing else marks these requests as authenticated.
    credentials: 'same-origin',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (res.status === 204) return null as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (body as { error?: string }).error ?? 'unknown');
  return body as T;
}

export async function loadConfig(): Promise<AuthConfig> {
  if (config) return config;
  config = await request<AuthConfig>('/auth/config');
  return config;
}

function identityErrorCode(code: string): string {
  switch (code) {
    case 'USER_DISABLED':
      return 'account_disabled';
    case 'TOO_MANY_ATTEMPTS_TRY_LATER':
      return 'too_many_attempts';
    case 'PASSWORD_LOGIN_DISABLED':
    case 'OPERATION_NOT_ALLOWED':
    case 'CONFIGURATION_NOT_FOUND':
      return 'auth_not_configured';
    default:
      if (/API key not valid|API_KEY_INVALID|not authorized/i.test(code)) {
        return 'auth_not_configured';
      }
      return 'unauthenticated';
  }
}

async function identityCall(url: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { error?: { message?: string } };
  if (!res.ok) {
    const raw = data.error?.message ?? 'UNKNOWN';
    throw new ApiError(401, identityErrorCode(String(raw).split(' : ')[0]!.trim()));
  }
  return data as Record<string, unknown>;
}

export interface SignInResult {
  kind: 'tenant' | 'admin';
}

/** Email/password → Identity Platform → our session cookie. In dev mode the
 *  email field carries a seeded uid and skips Identity Platform entirely. */
export async function signIn(email: string, password: string): Promise<SignInResult> {
  const cfg = await loadConfig();

  let idToken = email;
  if (cfg.driver === 'firebase') {
    if (!cfg.apiKey) throw new ApiError(500, 'auth_not_configured');
    const data = await identityCall(
      `${IDENTITY_URL}signInWithPassword?key=${encodeURIComponent(cfg.apiKey)}`,
      { email, password, returnSecureToken: true },
    );
    idToken = data.idToken as string;
  }

  return request<SignInResult>('/auth/session', { method: 'POST', body: { idToken } });
}

/** Google sends the reset email itself. One neutral outcome whether the
 *  address exists or not — anything else is an account-probing oracle. */
export async function sendPasswordReset(email: string): Promise<void> {
  const cfg = await loadConfig();
  if (cfg.driver !== 'firebase' || !cfg.apiKey) return;
  try {
    await identityCall(`${IDENTITY_URL}sendOobCode?key=${encodeURIComponent(cfg.apiKey)}`, {
      requestType: 'PASSWORD_RESET',
      email,
    });
  } catch (error) {
    if (error instanceof ApiError && error.code === 'unauthenticated') return; // EMAIL_NOT_FOUND
    throw error;
  }
}

export async function signOut(): Promise<void> {
  await request('/auth/session', { method: 'DELETE' }).catch(() => undefined);
}

/* --- Shapes the API returns ------------------------------------------------ */

export interface Me {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    roleLabel: string;
    status: string;
    initials: string;
    lastActiveAt: string | null;
  };
  company: { id: string; name: string; status: string; userCount: number };
}

export interface Property {
  id: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  county: string | null;
  phone: string | null;
  fax: string | null;
  ownerLlcName: string | null;
  ownerEin: string | null;
  stateProjectNumber: string | null;
  yearBuilt: number | null;
  totalUnits: number;
  fundingTypes: string[];
  monitoringAgency: string | null;
  status: string;
  createdAt: string;
  unitCount: number;
}

export interface Unit {
  id: string;
  unitNumber: string;
  addressLine1: string | null;
  bedrooms: number | null;
  squareFeet: number | null;
  isMarketRate: boolean;
  designation: number | null;
  monthlyRentCents: number | null;
  status: string;
}

export function money(cents: number | null): string {
  if (cents == null) return '—';
  return '$' + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function designationLabel(unit: Pick<Unit, 'isMarketRate' | 'designation'>): string {
  if (unit.isMarketRate) return 'Market';
  return unit.designation != null ? `${unit.designation}% AMI` : 'LIHTC';
}
